import { useState, useMemo } from 'react';
import AutocompleteDropdown from '../AutocompleteDropdown/AutocompleteDropdown';
import { formatCategoryName } from '../../util/RecUtil';

const INTERVENTION_CATEGORIES = [
    'physical_therapy',
    'exercise',
    'stretching_mobility',
    'sport_specific',
    'vision_training',
    'speech_therapy',
    'mindfulness_breathing',
];

const DROPDOWN_POSITION = { top: '100%', left: 0, right: 0 };

function AddInterventionRow({ onAdd }) {
    const [expanded, setExpanded] = useState(false);

    const [customFields, setCustomFields] = useState({
        name: '', dose: '', unit: 'min', frequency: '1', freqUnit: 'day',
        sets: '', category: '', instructions: '',
    });

    const [showCatDropdown, setShowCatDropdown] = useState(false);
    const [catSelectedIndex, setCatSelectedIndex] = useState(-1);

    const filteredCategories = useMemo(() => {
        const lower = customFields.category.toLowerCase();
        if (!lower) return INTERVENTION_CATEGORIES;
        return INTERVENTION_CATEGORIES.filter(c =>
            formatCategoryName(c).toLowerCase().includes(lower)
        );
    }, [customFields.category]);

    const catDisplayToSlug = useMemo(() => {
        const map = new Map();
        filteredCategories.forEach(c => map.set(formatCategoryName(c), c));
        return map;
    }, [filteredCategories]);

    const handleFieldChange = (field, value) => {
        setCustomFields(prev => ({ ...prev, [field]: value }));
        if (field === 'category') {
            setCatSelectedIndex(-1);
            setShowCatDropdown(true);
        }
    };

    const handleCatSelect = (slug) => {
        setCustomFields(prev => ({ ...prev, category: formatCategoryName(slug) }));
        setShowCatDropdown(false);
        setCatSelectedIndex(-1);
    };

    const handleCatKeyDown = (e) => {
        if (!showCatDropdown || filteredCategories.length === 0) return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setCatSelectedIndex(prev => prev < filteredCategories.length - 1 ? prev + 1 : prev);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setCatSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (catSelectedIndex >= 0 && filteredCategories[catSelectedIndex]) {
                    handleCatSelect(filteredCategories[catSelectedIndex]);
                }
                break;
            case 'Escape':
                setShowCatDropdown(false);
                setCatSelectedIndex(-1);
                break;
        }
    };

    const resetForm = () => {
        setCustomFields({
            name: '', dose: '', unit: 'min', frequency: '1', freqUnit: 'day',
            sets: '', category: '', instructions: '',
        });
        setShowCatDropdown(false);
        setCatSelectedIndex(-1);
    };

    const handleAdd = () => {
        if (!customFields.name.trim()) return;
        const entry = {
            name: customFields.name.trim(),
            type: 'intervention',
            category: customFields.category.trim() || 'uncategorized',
            dose: parseInt(customFields.dose) || 1,
            unit: customFields.unit || 'min',
            sets: customFields.sets ? parseInt(customFields.sets) : null,
            frequency: parseInt(customFields.frequency) || 1,
            freqUnit: customFields.freqUnit || 'day',
            emptyStomach: null,
            timeOfDay: null,
            instructions: customFields.instructions.trim() || null,
            notes: null,
            source: 'manual',
            addedBy: 'patient',
            addedAt: new Date().toISOString(),
        };
        onAdd(entry);
        resetForm();
    };

    const handleToggleExpand = () => {
        if (expanded) {
            setExpanded(false);
            resetForm();
        } else {
            setExpanded(true);
        }
    };

    return (
        <div className={`protocol-add-row ${expanded ? 'expanded' : ''}`}>
            <div className="protocol-add-header" onClick={handleToggleExpand}>
                <span className="protocol-add-icon">{expanded ? '\u2212' : '+'}</span>
                <span>Add activity</span>
                {expanded && (
                    <svg
                        className="protocol-add-collapse"
                        width="16" height="16" viewBox="0 0 16 16"
                        fill="none" stroke="currentColor" strokeWidth="1.5"
                    >
                        <path d="M4 10 L8 6 L12 10" />
                    </svg>
                )}
            </div>

            {expanded && (
                <div className="protocol-add-expanded">
                    <div className="protocol-add-custom-form">
                        <div className="protocol-add-custom-row">
                            <div className="protocol-add-custom-field" style={{ flex: 2 }}>
                                <label>Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Hip flexor stretches"
                                    value={customFields.name}
                                    onChange={(e) => handleFieldChange('name', e.target.value)}
                                />
                            </div>
                            <div className="protocol-add-custom-field" style={{ position: 'relative' }}>
                                <label>Category</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Physical Therapy"
                                    value={customFields.category}
                                    onChange={(e) => handleFieldChange('category', e.target.value)}
                                    onFocus={() => setShowCatDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowCatDropdown(false), 200)}
                                    onKeyDown={handleCatKeyDown}
                                />
                                {showCatDropdown && filteredCategories.length > 0 && (
                                    <AutocompleteDropdown
                                        suggestions={filteredCategories.map(formatCategoryName)}
                                        onSelect={(displayName) => {
                                            const raw = catDisplayToSlug.get(displayName);
                                            if (raw) handleCatSelect(raw);
                                        }}
                                        selectedIndex={catSelectedIndex}
                                        position={DROPDOWN_POSITION}
                                        onClose={() => setShowCatDropdown(false)}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="protocol-add-custom-row">
                            <div className="protocol-add-custom-field">
                                <label>Amount</label>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="1"
                                    value={customFields.dose}
                                    onChange={(e) => handleFieldChange('dose', e.target.value)}
                                />
                            </div>
                            <div className="protocol-add-custom-field">
                                <label>Unit</label>
                                <select
                                    value={customFields.unit}
                                    onChange={(e) => handleFieldChange('unit', e.target.value)}
                                >
                                    <option value="min">min</option>
                                    <option value="reps">reps</option>
                                    <option value="seconds">seconds</option>
                                </select>
                            </div>
                            <div className="protocol-add-custom-field">
                                <label>Sets</label>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="optional"
                                    value={customFields.sets}
                                    onChange={(e) => handleFieldChange('sets', e.target.value)}
                                />
                            </div>
                            <div className="protocol-add-custom-field">
                                <label>Frequency</label>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="1"
                                    value={customFields.frequency}
                                    onChange={(e) => handleFieldChange('frequency', e.target.value)}
                                />
                            </div>
                            <div className="protocol-add-custom-field">
                                <label>Per</label>
                                <select
                                    value={customFields.freqUnit}
                                    onChange={(e) => handleFieldChange('freqUnit', e.target.value)}
                                >
                                    <option value="day">day</option>
                                    <option value="week">week</option>
                                </select>
                            </div>
                        </div>
                        <div className="protocol-add-custom-row">
                            <div className="protocol-add-custom-field" style={{ flex: 1 }}>
                                <label>Instructions</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Hold 30 seconds each side"
                                    value={customFields.instructions}
                                    onChange={(e) => handleFieldChange('instructions', e.target.value)}
                                />
                            </div>
                        </div>
                        <button
                            className="protocol-add-custom-submit"
                            disabled={!customFields.name.trim()}
                            onClick={handleAdd}
                        >
                            Add to Protocol
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AddInterventionRow;
